import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Container } from "reactstrap";

import CompanySetupModal from "Pages/Admin/CompanySetup/CompanySetupModal/CompanySetupModal";
import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Admin/CompanySetup/CompanySetup.module.css";
import DefaultColumnFilter from "components/ReactTable/Components/DefaultColumnFilter/DefaultColumnFilter";
import { format } from "date-fns";
import usePageTitle from "Hooks/usePageTitle";

export type ActionType = "new" | "edit" | "";
interface ICompanySetup {
    company_id: string;
    name: string;
    enabled: boolean;
    systems: string;
    status: string;
    address: string;
    contact_emails: string;
    notes: string;
    created_date: Date;
}

export default function CompanySetup() {
    const [companyInfo, setCompanyInfo] = useState<ICompanySetup[]>([]);
    const [activeModal, setActiveModal] = useState<ActionType>("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState<ICompanySetup | undefined>(
        undefined
    );

    usePageTitle("Company Setup");
    const { get } = useFetch();

    const getCompanyInfo = useCallback(async () => {
        setIsLoading(true);

        const response = await get("/api/admin/company");

        let data: Response | ICompanySetup[];
        try {
            data = await response.json();
        } catch (e) {
            data = response;
        }

        if (response.ok) {
            data = data as ICompanySetup[];
            setCompanyInfo(data);
        }

        setIsLoading(false);
    }, [get]);

    useEffect(() => {
        getCompanyInfo();
    }, [getCompanyInfo]);

    /** Wraps state setting callback to pass to child component and update state from parent */
    const onSetActiveModal = useCallback((modal: ActionType) => {
        setActiveModal(modal);
    }, []);

    /** Wraps state setting callback to pass to child component and update state from parent */
    const onSetSelectedRow = useCallback((row: ICompanySetup) => {
        setSelectedRow(row);
    }, []);

    /** Passed down to table as rowDoubleClickCallback */
    const editRow = useCallback((row: ICompanySetup) => {
        setActiveModal("edit");
        setSelectedRow(row);
    }, []);

    const columns = useMemo(() => {
        const cellContent = (value: string) => (
            <span className={styles.textBox}>{value}</span>
        );
        const cellContentBool = (value: boolean) => (
            <span className={styles.textBox}>{value.toString()}</span>
        );

        return [
            {
                Header: "Company ID",
                accessor: "company_id",
                Cell: ({ value }: { value: string }) => cellContent(value),
                // Not using default filter, so as to allow for auto focusing this input
                Filter: ({ column }: any) => {
                    return <DefaultColumnFilter column={column} autoFocus />;
                },
            },
            {
                Header: "Name",
                accessor: "name",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Enabled",
                accessor: "enabled",
                Cell: ({ value }: { value: boolean }) => cellContentBool(value),
                // Filter: EnabledFilter(),
                Filter: ({ column }: { column: any }) => (
                    <EnabledFilter column={column} />
                ),
            },
            {
                Header: "Systems",
                accessor: "systems",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Address",
                accessor: "address",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Contact Emails",
                accessor: "contact_emails",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Notes",
                accessor: "notes",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Created Date",
                accessor: "created_date",
                Cell: ({ value }: { value: string }) =>
                    cellContent(
                        value
                            ? format(new Date(value), "MMM d, yyyy, HH:mm:ss")
                            : "null"
                    ),
                Filter: "",
            },
        ];
    }, []);

    const tableProps = {
        columns,
        data: companyInfo,
        showPagination: true,
        onSetSelectedRow,
        rowDoubleClickCallback: editRow,
        isLoading,
    };

    const companySetupModalProps = {
        activeModal,
        getCompanyInfo,
        onSetActiveModal,
        onSetSelectedRow,
        selectedRow,
    };

    return (
        <Container fluid>
            {/* Header and New/Edit/Delete buttons */}
            <div className={styles.headerAndButtonsBox}>
                <h1>Companies</h1>
                <span className={styles.buttonsBox}>
                    <Button
                        color="primary"
                        onClick={() => setActiveModal("new")}
                    >
                        New
                    </Button>
                    <Button
                        color="success"
                        onClick={() => setActiveModal("edit")}
                    >
                        Edit
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />

            <CompanySetupModal {...companySetupModalProps} />
        </Container>
    );
}

interface IFilterProps {
    column: any;
}

function EnabledFilter({ column }: IFilterProps) {
    type enabledFilterType = "All" | "True" | "False";
    const [enabledFilter, setEnabledFilter] =
        useState<enabledFilterType>("All");

    const handleEnabledFilterClick = async (column: any) => {
        const { setFilter } = column;

        if (enabledFilter === "All") {
            setEnabledFilter("True");
            setFilter("true");
        } else if (enabledFilter === "True") {
            setEnabledFilter("False");
            setFilter("false");
        } else {
            setEnabledFilter("All");
            setFilter("");
        }
    };

    const getEnabledFilterButtonColor = (filter: enabledFilterType): string => {
        if (filter === "True") {
            return "success";
        } else if (filter === "False") {
            return "danger";
        } else {
            return "primary";
        }
    };

    return (
        <Button
            style={{ width: "120px" }}
            id="filter_button"
            color={getEnabledFilterButtonColor(enabledFilter)}
            onClick={() => handleEnabledFilterClick(column)}
        >
            {enabledFilter}
        </Button>
    );
}
